import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Settings2, ArrowUp, ArrowDown, GripVertical, X, RotateCcw } from "lucide-react";
import { api } from "../lib/api.js";
import { VIEW_REGISTRY } from "../lib/columnConfig.js";

const TABLET_MAX_BREAKPOINT = 1024;

function useViewport() {
  const [w, setW] = useState(() => (typeof window === "undefined" ? 1920 : window.innerWidth));
  useEffect(() => {
    const onR = () => setW(window.innerWidth);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  return w;
}

export function useColumnConfig(viewKey) {
  const reg = VIEW_REGISTRY[viewKey];
  if (!reg) throw new Error(`Unknown view key: ${viewKey}`);
  const [visible, setVisible] = useState(reg.default);
  const [sortKey, setSortKey] = useState(reg.defaultSort?.key || null);
  const [sortDir, setSortDir] = useState(reg.defaultSort?.dir || "asc");
  const [widths, setWidths] = useState({});
  const [loaded, setLoaded] = useState(false);

  // Load saved config
  useEffect(() => {
    let cancelled = false;
    // Reset to the new registry's default whenever viewKey changes, so
    // stale state from a previous report doesn't leak into the current one
    // (fuel-consumption cols kept only the intersection with open-cases
    // otherwise, showing 3 columns instead of 13).
    setVisible(reg.default);
    setSortKey(reg.defaultSort?.key || null);
    setSortDir(reg.defaultSort?.dir || "asc");
    setWidths({});
    api.get(`/users/me/list-columns/${viewKey}`).then(({ data }) => {
      if (cancelled) return;
      if (data.visible && data.visible.length) setVisible(data.visible);
      if (data.sort_key) setSortKey(data.sort_key);
      if (data.sort_dir) setSortDir(data.sort_dir);
      if (data.widths) setWidths(data.widths);
      setLoaded(true);
    }).catch(() => setLoaded(true));
    return () => { cancelled = true; };
  }, [viewKey, reg]);

  const persist = useCallback(async (patch) => {
    try {
      await api.put(`/users/me/list-columns/${viewKey}`, patch);
    } catch (e) { /* ignore — local state already updated */ }
  }, [viewKey]);

  const setVisibleAndSave = useCallback((next) => {
    setVisible(next);
    persist({ visible: next });
  }, [persist]);

  const setSort = useCallback((key, dir) => {
    setSortKey(key);
    setSortDir(dir);
    persist({ sort_key: key, sort_dir: dir });
  }, [persist]);

  const setWidth = useCallback((id, w) => {
    setWidths((prev) => {
      const next = { ...prev, [id]: w };
      persist({ widths: next });
      return next;
    });
  }, [persist]);

  const reset = useCallback(async () => {
    await api.delete(`/users/me/list-columns/${viewKey}`);
    setVisible(reg.default);
    setSortKey(reg.defaultSort?.key || null);
    setSortDir(reg.defaultSort?.dir || "asc");
    setWidths({});
  }, [viewKey, reg]);

  return {
    registry: reg,
    visible, setVisible: setVisibleAndSave,
    sortKey, sortDir, setSort,
    widths, setWidth,
    reset, loaded,
  };
}

export function ColumnConfigButton({ config, viewKey }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-xs border border-gray-300 hover:border-[#1F3864] rounded-sm px-2 py-1 text-[#1F3864]"
        data-testid={`col-config-btn-${viewKey}`}
        title="Configurar colunas"
      >
        <Settings2 className="w-3.5 h-3.5" /> Colunas
      </button>
      {open && <ColumnConfigPanel config={config} viewKey={viewKey} onClose={() => setOpen(false)} />}
    </>
  );
}

function ColumnConfigPanel({ config, viewKey, onClose }) {
  const { registry, visible, setVisible, reset } = config;
  const all = registry.columns;
  // Local state for editing
  const [draft, setDraft] = useState(visible);
  const dragId = useRef(null);

  const isVisible = (id) => draft.includes(id);
  const toggle = (id) => {
    if (isVisible(id)) {
      if (draft.length === 1) return; // keep at least one
      setDraft(draft.filter((x) => x !== id));
    } else {
      setDraft([...draft, id]);
    }
  };
  const move = (fromId, toId) => {
    if (fromId === toId) return;
    const next = [...draft];
    const fromIdx = next.indexOf(fromId);
    const toIdx = next.indexOf(toId);
    if (fromIdx < 0 || toIdx < 0) return;
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, fromId);
    setDraft(next);
  };

  const apply = () => { setVisible(draft); onClose(); };

  const visibleEntries = draft.map((id) => all.find((c) => c.id === id)).filter(Boolean);
  const hiddenEntries = all.filter((c) => !draft.includes(c.id));

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div className="bg-white w-full max-w-sm h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()} data-testid={`col-config-panel-${viewKey}`}>
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Configurar colunas</div>
            <h2 className="font-heading text-base font-bold text-[#1A1A2E]">{visibleEntries.length} visíveis · {hiddenEntries.length} ocultas</h2>
          </div>
          <button onClick={onClose} className="text-[#6C757D] hover:text-[#1A1A2E]"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-2">Visíveis — arraste para reordenar</div>
          <ul className="space-y-1 mb-4">
            {visibleEntries.map((c) => (
              <li
                key={c.id}
                draggable
                onDragStart={() => { dragId.current = c.id; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragId.current) move(dragId.current, c.id); dragId.current = null; }}
                className="flex items-center gap-2 p-2 bg-[#F8FAFC] border border-gray-200 rounded-sm hover:border-[#1F3864] cursor-move"
                data-testid={`col-config-visible-${c.id}`}
              >
                <GripVertical className="w-3.5 h-3.5 text-[#94A3B8]" />
                <input type="checkbox" checked readOnly onClick={() => toggle(c.id)} className="cursor-pointer" />
                <span className="text-sm text-[#1A1A2E]">{c.label}</span>
              </li>
            ))}
          </ul>

          <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-2">Ocultas — marque para adicionar</div>
          <ul className="space-y-1">
            {hiddenEntries.map((c) => (
              <li
                key={c.id}
                className="flex items-center gap-2 p-2 border border-gray-200 rounded-sm hover:border-[#1F3864] cursor-pointer"
                onClick={() => toggle(c.id)}
                data-testid={`col-config-hidden-${c.id}`}
              >
                <span className="w-3.5" />
                <input type="checkbox" checked={false} onChange={() => toggle(c.id)} />
                <span className="text-sm text-[#6C757D]">{c.label}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between">
          <button
            onClick={async () => { await reset(); onClose(); }}
            className="inline-flex items-center gap-1 text-xs text-[#6C757D] hover:text-[#1F3864]"
            data-testid={`col-config-reset-${viewKey}`}
          >
            <RotateCcw className="w-3 h-3" /> Repor por defeito
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-xs px-3 py-1.5 border border-gray-300 rounded-sm hover:bg-[#F4F6F8]">Cancelar</button>
            <button onClick={apply} className="text-xs px-3 py-1.5 bg-[#1F3864] text-white rounded-sm hover:bg-[#142542]" data-testid={`col-config-apply-${viewKey}`}>
              Aplicar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ConfigurableTable({ viewKey, config, rows, getRowId = (r) => r.id, emptyMessage = "Sem registos.", rowContext = null, getRowClassName = null }) {
  const { registry, visible, setVisible, sortKey, sortDir, setSort, widths, setWidth } = config;
  const viewportWidth = useViewport();
  const isTablet = viewportWidth < TABLET_MAX_BREAKPOINT;
  const colDefs = useMemo(() => {
    const all = registry.columns;
    let chosen = visible.map((id) => all.find((c) => c.id === id)).filter(Boolean);
    if (isTablet && chosen.length > 6) chosen = chosen.slice(0, 6);
    return chosen;
  }, [visible, registry, isTablet]);

  // Total width used to force horizontal scroll when the sum of columns exceeds
  // the container. Without this `<table class="w-full">` collapses columns to
  // fit and `overflow-x-auto` never activates.
  const totalMinWidth = useMemo(
    () => colDefs.reduce((sum, c) => sum + (widths[c.id] || c.defaultWidth || 140), 0),
    [colDefs, widths],
  );

  // Drag-and-drop reorder directly on column headers.
  const dragFromId = useRef(null);
  const justDraggedRef = useRef(false);
  const [dragOverId, setDragOverId] = useState(null);
  const moveColumn = useCallback((fromId, toId) => {
    if (!fromId || fromId === toId) return;
    const current = colDefs.map((c) => c.id);
    const next = [...current];
    const fromIdx = next.indexOf(fromId);
    const toIdx = next.indexOf(toId);
    if (fromIdx < 0 || toIdx < 0) return;
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, fromId);
    // Preserve any hidden columns at the end
    const hidden = visible.filter((id) => !next.includes(id));
    setVisible([...next, ...hidden]);
  }, [colDefs, visible, setVisible]);

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const col = registry.columns.find((c) => c.id === sortKey);
    if (!col || col.sortable !== true) return rows;
    const val = (r) => {
      const v = col.accessor ? col.accessor(r, rowContext) : r[sortKey];
      if (v == null || v === "—") return null;
      return v;
    };
    const arr = [...rows];
    arr.sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1; // nulls last
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return sortDir === "asc" ? va - vb : vb - va;
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
    return arr;
  }, [rows, sortKey, sortDir, registry, rowContext]);

  const onHeaderClick = (col) => {
    if (col.sortable !== true) return;
    if (sortKey === col.id) {
      setSort(col.id, sortDir === "asc" ? "desc" : "asc");
    } else {
      setSort(col.id, "asc");
    }
  };

  // Floating horizontal scrollbar — syncs with the real table wrapper so the
  // user can pan sideways without scrolling the page down to find the native
  // scrollbar at the bottom of a long list. Only shown when the table actually
  // overflows horizontally.
  const bodyScrollRef = useRef(null);
  const topScrollRef = useRef(null);
  const syncing = useRef(false);
  const [overflow, setOverflow] = useState({ scrollWidth: 0, clientWidth: 0 });

  useEffect(() => {
    const measure = () => {
      const el = bodyScrollRef.current;
      if (!el) return;
      setOverflow({ scrollWidth: el.scrollWidth, clientWidth: el.clientWidth });
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro && bodyScrollRef.current) ro.observe(bodyScrollRef.current);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
    };
  }, [totalMinWidth, sorted.length]);

  const onBodyScroll = () => {
    if (syncing.current) return;
    syncing.current = true;
    if (topScrollRef.current && bodyScrollRef.current) {
      topScrollRef.current.scrollLeft = bodyScrollRef.current.scrollLeft;
    }
    // Release the lock on the next frame — scroll events fire asynchronously
    // after setting scrollLeft, so we must outlive the reciprocal handler.
    requestAnimationFrame(() => { syncing.current = false; });
  };
  const onTopScroll = () => {
    if (syncing.current) return;
    syncing.current = true;
    if (bodyScrollRef.current && topScrollRef.current) {
      bodyScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
    requestAnimationFrame(() => { syncing.current = false; });
  };
  const isOverflowing = overflow.scrollWidth > overflow.clientWidth + 1;

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-soft-card">
      {/* Floating horizontal scrollbar — sticky at the top of the table viewport
          so users can pan sideways without scrolling the page to the bottom. */}
      {isOverflowing && (
        <div
          ref={topScrollRef}
          onScroll={onTopScroll}
          data-testid={`floating-scrollbar-${viewKey}`}
          className="sticky top-0 z-20 overflow-x-scroll bg-slate-50 border-b border-slate-200"
          style={{ height: 14 }}
        >
          <div style={{ width: overflow.scrollWidth, height: 1 }} />
        </div>
      )}
      <div ref={bodyScrollRef} onScroll={onBodyScroll} className="overflow-x-auto">
        <table
          className="text-sm"
          style={{ minWidth: totalMinWidth, width: "100%" }}
          data-testid={`configurable-table-${viewKey}`}
        >
          <thead className="bg-slate-50 text-[10px] font-heading uppercase tracking-[0.1em] text-slate-500 border-b border-slate-200">
            <tr>
              {colDefs.map((c) => {
                const w = widths[c.id] || c.defaultWidth || 140;
                const isSorted = sortKey === c.id;
                const isDragOver = dragOverId === c.id;
                return (
                  <th
                    key={c.id}
                    draggable
                    onDragStart={(e) => {
                      dragFromId.current = c.id;
                      try { e.dataTransfer.effectAllowed = "move"; } catch {}
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (dragFromId.current && dragFromId.current !== c.id) setDragOverId(c.id);
                    }}
                    onDragLeave={() => { if (dragOverId === c.id) setDragOverId(null); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      moveColumn(dragFromId.current, c.id);
                      dragFromId.current = null;
                      setDragOverId(null);
                      justDraggedRef.current = true;
                      setTimeout(() => { justDraggedRef.current = false; }, 300);
                    }}
                    onDragEnd={() => { dragFromId.current = null; setDragOverId(null); }}
                    className={`relative px-3 py-2.5 text-left select-none cursor-move font-heading font-semibold ${c.sortable ? "hover:bg-slate-100" : ""} ${isDragOver ? "bg-ronil-navy-50 border-l-2 border-ronil-navy" : ""} ${c.headerClass || ""}`}
                    style={{ width: w, minWidth: w }}
                    onClick={() => { if (!justDraggedRef.current) onHeaderClick(c); }}
                    data-testid={`col-header-${c.id}`}
                    title="Arraste para reordenar · Clique para ordenar"
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {isSorted && (sortDir === "asc" ? <ArrowUp className="w-3 h-3 text-ronil-navy" /> : <ArrowDown className="w-3 h-3 text-ronil-navy" />)}
                    </span>
                    <ColumnResizer id={c.id} currentWidth={w} onResize={setWidth} />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={colDefs.length} className="px-3 py-10 text-center text-slate-400 italic">{emptyMessage}</td></tr>
            ) : sorted.map((row, rowIdx) => {
              const extraCls = getRowClassName ? getRowClassName(row, rowContext) : "";
              return (
              <tr
                key={getRowId(row, rowIdx)}
                className={`border-b border-slate-100 last:border-0 ${rowIdx % 2 === 1 ? "bg-slate-50/40" : ""} hover:bg-ronil-navy-50/60 transition-colors ${extraCls}`}
                data-testid={`row-${getRowId(row, rowIdx)}`}
              >
                {colDefs.map((c) => {
                  const content = c.render ? c.render(row, rowContext) : (c.accessor ? c.accessor(row, rowContext) : row[c.id]);
                  const tooltip = typeof content === "string" || typeof content === "number" ? String(content) : undefined;
                  return (
                    <td
                      key={c.id}
                      className={`px-3 py-2.5 truncate text-slate-700 ${c.cellClass || ""}`}
                      style={{ maxWidth: widths[c.id] || c.defaultWidth || 140 }}
                      title={tooltip}
                    >
                      {content ?? "—"}
                    </td>
                  );
                })}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ColumnResizer({ id, currentWidth, onResize }) {
  const startX = useRef(null);
  const startW = useRef(null);
  const onDown = (e) => {
    // Prevent the parent <th>'s native HTML5 drag from starting when the user
    // grabs the resize handle, and stop click-to-sort from firing afterwards.
    e.preventDefault();
    e.stopPropagation();
    startX.current = e.clientX;
    startW.current = currentWidth;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp, { once: true });
  };
  const onMove = (e) => {
    if (startX.current == null) return;
    const delta = e.clientX - startX.current;
    const newW = Math.max(60, startW.current + delta);
    onResize(id, newW);
  };
  const onUp = () => {
    startX.current = null;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    document.removeEventListener("mousemove", onMove);
  };
  return (
    <span
      onMouseDown={onDown}
      onClick={(e) => e.stopPropagation()}
      onDragStart={(e) => e.preventDefault()}
      draggable={false}
      className="absolute right-0 top-1 bottom-1 w-2 cursor-col-resize bg-transparent hover:bg-[#1F3864]/60 border-r border-gray-300/60 transition-colors"
      style={{ touchAction: "none" }}
      data-testid={`col-resizer-${id}`}
      title="Arraste para redimensionar"
    />
  );
}
